import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode
} from 'react'

const STORAGE_KEY = 'floor-copilot-theme'

export type OperatorTheme = 'light' | 'dark'

function readStoredTheme(): OperatorTheme {
  if (typeof localStorage === 'undefined') return 'dark'
  return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark'
}

interface OperatorThemeContextValue {
  theme: OperatorTheme
  isDark: boolean
  toggleTheme: () => void
  setTheme: (theme: OperatorTheme) => void
}

const OperatorThemeContext = createContext<OperatorThemeContextValue | null>(null)

export function OperatorThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<OperatorTheme>(() => readStoredTheme())

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const html = document.documentElement
    const previous = html.getAttribute('data-theme')
    return () => {
      if (previous === null) html.removeAttribute('data-theme')
      else html.setAttribute('data-theme', previous)
    }
  }, [])

  const setTheme = useCallback((next: OperatorTheme) => {
    setThemeState(next)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === 'dark' ? 'light' : 'dark'))
  }, [])

  return createElement(
    OperatorThemeContext.Provider,
    { value: { theme, isDark: theme === 'dark', toggleTheme, setTheme } },
    children
  )
}

/** Persisted light/dark preference for the floor copilot shell. */
export function useOperatorTheme(): OperatorThemeContextValue {
  const ctx = useContext(OperatorThemeContext)
  if (!ctx) {
    throw new Error('useOperatorTheme must be used within OperatorThemeProvider')
  }
  return ctx
}
